import {
  MDBBtn,
  MDBBtnGroup,
  MDBCard,
  MDBCardBody,
  MDBCardImage,
  MDBCardText,
  MDBCardTitle,
  MDBCol,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBRow,
  MDBTooltip,
} from 'mdb-react-ui-kit';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { UserProfileContext } from '../context/UserProfileProvider';
import { deepEqual } from '../utils/Common';
import FeatureForm from './FeatureForm';
import RecursiveFeature from './RecursiveFeature';

export const FeatureList = ({
  formData,
  demoId,
  isEdit,
  handleChange,
  handleAddImageUrl,
  handleRemoveImageUrl,
  handleImageUrlChange,
  handlePainKeyChange,
  handlePainPromiseChange,
  addPainPromise,
  removePainPromise,
  removeImpact,
  addImpact,
  activeAccordions,
  setActiveAccordions,
  demoUser,
  handleCheckboxChange,
  addCheckbox,
  removeCheckbox,
  handleCheckboxLabelChange,
  addQuestion,
  removeQuestion,
  handleQuestionInputChange,
  handleAnswerInputChange,
  handleFeedbackUserChange,
  removeFeedback,
  addFeature,
  removeFeature,
  handleUpdateForNonLoggedinFeedBackUser,
  addFeedback,
  addSubFeature,
  removeSubFeature,
  setCentredModal,
  centredModal,
  selectedFeature,
setSelectedFeature,
handleCloseModal,
setFormData,
activeFeatureIndex,
setActiveFeatureIndex
}) => {
  const { tokenLoaded } = useContext(UserProfileContext);
 
  
  const [modalOpen, setModalOpen] = useState(false); // Additional state to manage nested modals

  const toggleFeatureListModal = () => setCentredModal(!centredModal);
  const toggleModal = () => setModalOpen(!modalOpen);



  

  // Calculate column sizes based on the number of features
  const calculateColumnSize = () => {
    const count = formData?.orchestration?.length || 0;
    if (count === 1) return { lg: 12, md: 12 }; // Full width if only 1 card
    if (count === 2) return { lg: 6, md: 6 }; // Half width if 2 cards
    return { lg: 4, md: 6 }; // One-third width if more than 2 cards
  };

  const columnSize = calculateColumnSize();

  const handleSwapFeatures = (index1, index2) => {
    setFormData((prevState) => {
      const updatedOrchestration = [...prevState.orchestration];
  
      // Ensure index2 is within bounds
      if (index2 < 0 || index2 >= updatedOrchestration.length) return prevState;
  
      // Swap features at index1 and index2
      [updatedOrchestration[index1], updatedOrchestration[index2]] = [updatedOrchestration[index2], updatedOrchestration[index1]];
  
      // Update slideNumber based on new order
      updatedOrchestration.forEach((feature, idx) => {
        feature.slideNumber = idx + 1;
      });
  
      return { ...prevState, orchestration: updatedOrchestration };
    });
  };
  
  const moveToTop = (index) => {
    if (index > 0) {
      setFormData((prevState) => {
        const updatedOrchestration = [...prevState.orchestration];
  
        // Move the feature to the top
        const [movedFeature] = updatedOrchestration.splice(index, 1);
        updatedOrchestration.unshift(movedFeature);
  
        // Update slideNumber for all features
        updatedOrchestration.forEach((feature, idx) => {
          feature.slideNumber = idx + 1;
        });
  
        return { ...prevState, orchestration: updatedOrchestration };
      });
    }
  };
  
  const moveToBottom = (index) => {
    if (index < formData.orchestration.length - 1) {
      setFormData((prevState) => {
        const updatedOrchestration = [...prevState.orchestration];
  
        // Move the feature to the bottom
        const [movedFeature] = updatedOrchestration.splice(index, 1);
        updatedOrchestration.push(movedFeature);
  
        // Update slideNumber for all features
        updatedOrchestration.forEach((feature, idx) => {
          feature.slideNumber = idx + 1;
        });
  
        return { ...prevState, orchestration: updatedOrchestration };
      });
    }
  };
  
  const moveUp = (index) => {
    if (index > 0) {
      handleSwapFeatures(index, index - 1);
    }
  };
  
  const moveDown = (index) => {
    if (index < formData.orchestration.length - 1) {
      handleSwapFeatures(index, index + 1);
    }
  };
  
  
    
  const getFeatureByPath = (orchestration, path) => {
    // Convert path to string if it's a number
    const pathString = typeof path === 'number' ? path.toString() : path;
  
    if (typeof pathString !== 'string') {
      console.error("Invalid path type:", pathString);
      return null;
    }
  
    // Split the path into indexes (for example, "0-1" => [0, 1])
    const indexes = pathString.split('-').map((i) => parseInt(i, 10));
  
    // Start at the root feature
    let currentFeature = orchestration[indexes[0]];
  
    // Traverse through subfeatures if there are further indexes
    for (let i = 1; i < indexes.length; i++) {
      if (currentFeature && currentFeature.subFeatures) {
        currentFeature = currentFeature.subFeatures[indexes[i]];
      } else {
        return null; // If no subfeatures exist or path is invalid, return null
      }
    }
  
    return currentFeature; // Return the final feature or subfeature
  };
 
  
  useEffect(() => {
    if (!formData?.orchestration || formData.orchestration.length === 0) {
      return; // Exit early if orchestration doesn't exist or is empty
    }
  
    const sortedOrchestration = [...formData.orchestration].sort((a, b) => a.slideNumber - b.slideNumber);
    const currentFeature = getFeatureByPath(formData.orchestration, activeFeatureIndex); // Use path to find feature
    
    if (!selectedFeature || !deepEqual(selectedFeature, currentFeature)) {
      setSelectedFeature(currentFeature);
    }
  }, [formData.orchestration, activeFeatureIndex, selectedFeature, setSelectedFeature]);
  

  const handleFeatureSelection = useCallback(
    (e, feature, index) => {
      e.preventDefault();
      
      // Reset selectedFeature first to avoid stale data
      setSelectedFeature(null);
      
      // Then set the new selected feature
      setSelectedFeature(feature);
      setActiveFeatureIndex(index); // Pass the full path
      
      // Open the modal
      setCentredModal(true);
    },
    [setActiveFeatureIndex, setCentredModal, setSelectedFeature]
  );
  



  return (
    <div className="container my-5 py-5 text-center align-items-center justify-content-center">
      {/* Section Heading */}
      <h2 className="fw-bold mb-7 text-center">{formData.name}</h2>

      {/* Feature Grid */}
      <MDBRow className={`gx-lg-5 d-flex ${formData?.orchestration?.length < 3 ? 'justify-content-center' : 'align-items-center justify-content-center'}`}>
        {formData?.orchestration?.slice() // Make a copy of the array to avoid mutating the original array
    .sort((a, b) => a.slideNumber - b.slideNumber) // Sort by slideNumber
    .map((feature, index) => (
      <MDBCol
      lg={columnSize.lg}
      md={columnSize.md}
      className="mb-5 mt-5 mb-lg-0"
      key={`feature-card-${feature.id || index}`}  // Use a unique identifier here
    >
    
            <MDBCard className="shadow-2-strong "   shadow="1">
            
                <div
                  className="bg-image hover-overlay shadow-4-strong rounded mx-3"
                  data-mdb-ripple-color="light"
                  style={{ marginTop: '-15px' }}
                >
                  {/* Feature Image */}
                  <MDBCardImage
                    src={feature.imageUrl || 'https://opengrit.com/images/defaultakada'}
                    className="img-fluid"
                    style={{ maxHeight: '200px' }}
                  />
                  <a href="#!">
                    <div className="mask" style={{ backgroundColor: 'rgba(251, 251, 251, 0.15)' }}></div>
                  </a>
                </div>
             
              {/* Card Body */}
              <MDBCardBody>
                <MDBCardTitle>{feature.name || 'Feature Title'}</MDBCardTitle>
                <MDBCardText>
                  {feature?.paragrapDescriptions && feature?.paragrapDescriptions.length > 0
                    ? feature?.paragrapDescriptions.sort((a, b) => a.orderNumber - b.orderNumber)[0].heading?.textValue || feature?.paragrapDescriptions[0]?.textValue
                    : ''}
                </MDBCardText>
             
   {tokenLoaded && isEdit &&      <MDBBtnGroup className='gap-1'>      <MDBBtn onClick={() => moveToTop(index)} disabled={index === 0} type='button'>
  <MDBIcon fas icon="angle-double-up" /> 
</MDBBtn>
<MDBBtn onClick={() => moveUp(index)} disabled={index === 0} type='button'>
  <MDBIcon fas icon="arrow-up" />
</MDBBtn>
<MDBBtn onClick={() => moveDown(index)} disabled={index === formData.orchestration.length - 1} type='button'>
  <MDBIcon fas icon="arrow-down" />
</MDBBtn>
<MDBBtn onClick={() => moveToBottom(index)} disabled={index === formData.orchestration.length - 1} type='button'>
  <MDBIcon fas icon="angle-double-down" />
</MDBBtn>
</MDBBtnGroup> 
}


                {feature.subFeatures && feature.subFeatures.length > 0 && (
                  <div className="sub-feature-section mt-3">
                   
                    {feature.subFeatures.map((subFeature, subIndex) => (
                      <RecursiveFeature
                        key={`subFeature-${index}-${subIndex}`}
                        feature={subFeature}
                        index={subIndex}
                        parentPath={index}
                        handleRemoveSubFeature={(subFeatureIndex) => removeSubFeature(index, subFeatureIndex)}
                        setSelectedFeature={setSelectedFeature}
                        setActiveFeatureIndex={setActiveFeatureIndex}
                        toggleFeatureListModal={toggleFeatureListModal}
                        setFormData={setFormData}
                        formData={formData}
                        isEdit={isEdit}
                      />
                    ))}
                  </div>
                )}



<MDBBtnGroup className="align-items-center justify-content-between gap-1">
  <MDBBtn
    floating
    size="lg"
    type="button"
    tag="a"
    color="none"
    style={{ color: '#dd4b39' }} // Button color
    onClick={(e) =>{ e.preventDefault(); handleFeatureSelection(e, feature, index)}} // Use a separate handler
  >
    <MDBTooltip tag="a" wrapperProps={{ href: '#' }} title="Read More">
      <MDBIcon fab icon="readme" style={{ color: '#dd4b39' }} /> {/* Icon color */}
    </MDBTooltip>
  </MDBBtn>
  {isEdit &&   <MDBBtn
    floating
    size="lg"
    type="button"
    tag="a"
    color="none"
    style={{ color: '#dd4b39' }} // Button color
    onClick={(e) =>{ e.preventDefault(); removeFeature(index)}} // Use a separate handler
  >
    <MDBTooltip tag="a" wrapperProps={{ href: '#' }} title="Remove Feature">
      <MDBIcon far icon="times-circle" style={{ color: '#dd4b39' }} /> {/* Icon color */}
    </MDBTooltip>
  </MDBBtn>}
</MDBBtnGroup>

              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        ))}
      </MDBRow>

      {/* Modal for Feature Details */}
      <MDBModal tabIndex="-1" open={centredModal} onClose={() => {setCentredModal(false); handleCloseModal()}}>
        <MDBModalDialog centered size="fullscreen">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{selectedFeature?.name || 'Feature Details'}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => {toggleFeatureListModal(); handleCloseModal()}} type="button"></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody className='text-center align-items-center justify-content-center'>
              {selectedFeature && (
                <FeatureForm
                demoId={demoId}
                  feature={selectedFeature}
                  featureIndex={activeFeatureIndex} 
                  isEdit={isEdit}
                  handleChange={handleChange}
                  handleAddImageUrl={handleAddImageUrl}
                  handleRemoveImageUrl={handleRemoveImageUrl}
                  handleImageUrlChange={handleImageUrlChange}
                  handlePainKeyChange={handlePainKeyChange}
                  handlePainPromiseChange={handlePainPromiseChange}
                  addPainPromise={addPainPromise}
                  removePainPromise={removePainPromise}
                  removeImpact={removeImpact}
                  addImpact={addImpact}
                  activeAccordions={activeAccordions}
                  setActiveAccordions={setActiveAccordions}
                  demoUser={demoUser}
                  handleCheckboxChange={handleCheckboxChange}
                  addCheckbox={addCheckbox}
                  removeCheckbox={removeCheckbox}
                  handleCheckboxLabelChange={handleCheckboxLabelChange}
                  addQuestion={addQuestion}
                  removeQuestion={removeQuestion}
                  handleQuestionInputChange={handleQuestionInputChange}
                  handleAnswerInputChange={handleAnswerInputChange}
                  handleFeedbackUserChange={handleFeedbackUserChange}
                  removeFeedback={removeFeedback}
                  toggleModal={toggleModal}
                  modalOpen={modalOpen}
                  handleUpdateForNonLoggedinFeedBackUser={handleUpdateForNonLoggedinFeedBackUser}
                  addFeedback={addFeedback}
                  addSubFeature={addSubFeature}
                  removeSubFeature={removeSubFeature}
                  setFormData={setFormData}
                  setCentredModal={setCentredModal}
                  formData={formData}
                  handleCloseModal={handleCloseModal}
                  setSelectedFeature={setSelectedFeature}
                  
                />
              )}
            </MDBModalBody>
            <MDBModalFooter className='text-center align-items-center justify-content-center' >

  <MDBBtnGroup size='sm' className='gap-1'> 



 
          <MDBBtn type="button" color="warning" onClick={() => {setCentredModal(false); setModalOpen(false); handleCloseModal()}}>
                Close
              </MDBBtn>
            {tokenLoaded && isEdit && <MDBBtn  onClick={() => {setCentredModal(false);handleCloseModal()}}>Save changes</MDBBtn>}
  </MDBBtnGroup>

              
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  );
};



FeatureList.propTypes = {
  formData: PropTypes.shape({
    name: PropTypes.string,
    orchestration: PropTypes.arrayOf(
      PropTypes.shape({
        idemoId: PropTypes.string,
        name: PropTypes.string,
        paragrapDescriptions: PropTypes.arrayOf(
          PropTypes.shape({
            textValue: PropTypes.string,
            orderNumber: PropTypes.number,
            paragraphs: PropTypes.array,
            heading: PropTypes.object,
          })
        ),
        subFeatures: PropTypes.array,
        imageUrl: PropTypes.string,
        imageType: PropTypes.string,
      })
    ),
  }).isRequired,
  isEdit: PropTypes.bool,
  handleChange: PropTypes.func,
  handleAddImageUrl: PropTypes.func,
  handleRemoveImageUrl: PropTypes.func,
  handleImageUrlChange: PropTypes.func,
  handlePainKeyChange: PropTypes.func,
  handlePainPromiseChange: PropTypes.func,
  addPainPromise: PropTypes.func,
  removePainPromise: PropTypes.func,
  removeImpact: PropTypes.func,
  addImpact: PropTypes.func,
  activeAccordions: PropTypes.array,
  setActiveAccordions: PropTypes.func,
  demoUser: PropTypes.object,
  handleCheckboxChange: PropTypes.func,
  addCheckbox: PropTypes.func,
  removeCheckbox: PropTypes.func,
  handleCheckboxLabelChange: PropTypes.func,
  addQuestion: PropTypes.func,
  removeQuestion: PropTypes.func,
  handleQuestionInputChange: PropTypes.func,
  handleAnswerInputChange: PropTypes.func,
  handleFeedbackUserChange: PropTypes.func,
  removeFeedback: PropTypes.func,
  addFeature: PropTypes.func,
  removeFeature: PropTypes.func,
  handleUpdateForNonLoggedinFeedBackUser: PropTypes.func,
  addSubFeature: PropTypes.func,
  removeSubFeature: PropTypes.func,
  demoId: PropTypes.string,
  addFeedback: PropTypes.func,
  setFormData: PropTypes.func,
  setCentredModal:PropTypes.func,
  centredModal:PropTypes.bool,
  selectedFeature:PropTypes.func,
setSelectedFeature: PropTypes.func,
handleCloseModal: PropTypes.func,
activeFeatureIndex:PropTypes.number,
setActiveFeatureIndex:PropTypes.func,
};

export default FeatureList;
